import type { Messages } from '@/areas/employee/i18n/de';

/**
 * Employee-portal messages — English. Typed against the German reference, so a
 * missing or misspelled key fails the build.
 */
const en: Messages = {
  profile: {
    heading: 'Settings',
    subtitle: 'Manage your profile, security and notifications.',
    tabs: {
      profile: 'Profile',
      security: 'Security',
      notifications: 'Notifications',
    },
    card: {
      profile: 'Profile',
      account: 'Account',
      language: 'Language',
      security: 'Change password',
    },
    fields: {
      username: 'Username',
      email: 'Email',
      extension: 'Extension',
      extensionHint: 'Two digits.',
      description: 'Description',
      phone1: 'Phone 1',
      phone2: 'Phone 2',
      mobile: 'Mobile',
      creationDate: 'Created',
      lastLogin: 'Last login',
      neverLoggedIn: 'Never',
    },
    directoryNote: 'Username and email come from the directory and cannot be changed here.',
    save: {
      submit: 'Save',
      unsaved: 'Unsaved changes',
      saved: 'Profile saved',
      savedDetail: 'Your profile changes have been saved.',
      saveFailed: 'Your profile could not be saved. Please try again.',
      loadFailed: 'Your profile could not be loaded. Please try again.',
    },
    account: {
      portal: 'Portal',
      roles: 'Roles',
      noRoles: 'No roles assigned.',
    },
    portal: {
      employee: 'Employee portal',
      customer: 'Customer portal',
    },
    language: {
      description: 'Sets the user-interface language for your account.',
      saved: 'Language changed',
      savedDetail: 'Your language preference has been saved.',
      saveFailed: 'The language could not be changed. Please try again.',
    },
    password: {
      ldapNote:
        'Your password is managed through the directory (LDAP) and cannot be changed here.',
      current: 'Current password',
      next: 'New password',
      confirm: 'Confirm password',
      minChars: 'At least {min} characters.',
      mismatch: 'The passwords do not match.',
      tooShort: 'The new password must be at least {min} characters long.',
      submit: 'Change password',
      changeFailed: 'Change failed. Please try again.',
      saved: 'Password changed',
      savedDetail: 'Your password has been updated.',
    },
    notifications: {
      unavailable: 'Notification settings are not available yet.',
    },
  },
  dashboard: {
    whitelist: 'Whitelist',
    blacklist: 'Blacklist',
    stats: {
      callsToday: 'Calls today',
      numbers: 'Numbers',
    },
    check: {
      title: 'Check phone number',
      submit: 'Check number',
      failed: 'Check failed. Please try again.',
      resultTitle: 'Check result',
      checkedNumber: 'Checked number',
      noEntry: 'No entry',
      close: 'Close',
      whitelistWithHint: 'Whitelist ({hint})',
      blacklistWithHint: 'Blacklist ({hint})',
    },
    direction: {
      all: 'All',
      inbound: 'Inbound',
      outbound: 'Outbound',
      filterLabel: 'Filter by direction',
    },
    match: {
      direct: 'direct entry',
      customer: 'Customer',
      user: 'User',
    },
    recent: {
      title: 'Recent calls',
      searchPlaceholder: 'Number, contact or customer…',
      ownOnly: 'Own calls only',
      empty: 'No calls found.',
      columns: {
        timestamp: 'Time',
        direction: 'Direction',
        number: 'Number',
        contact: 'Contact',
        customer: 'Customer',
        agent: 'Answered by',
        duration: 'Duration',
        action: 'Action',
      },
    },
  },
  nav: {
    overview: 'Overview',
    dashboard: 'Dashboard',
    customersGroup: 'Customers',
    customers: 'Customers',
    whitelist: 'Call filter',
    profile: 'Profile',
    home: 'Home',
    brandSub: 'Employee Console',
    orgPlan: 'Employee access',
  },
  customers: {
    title: 'Customers',
    subtitle: 'Customer master data — read-only, maintained via WISO MeinBüro',
    results: '{count} customers',
    empty: 'No customers found.',
    loadFailed: 'Failed to load customers.',
    open: 'Open customer',
    columns: {
      number: 'Number',
      name: 'Name',
      street: 'Street',
      zip: 'Zip',
      city: 'City',
      type: 'Type',
    },
    filter: {
      number: 'Filter number',
      name: 'Filter name',
      street: 'Filter street',
      zip: 'Filter zip',
      city: 'Filter city',
      typeAll: 'All types',
    },
    type: {
      CUSTOMER: 'Customer',
      SUPPLIER: 'Supplier',
      INTERESTED_PARTY: 'Interested party',
      UNKNOWN: 'Unknown',
    },
    detail: {
      title: 'Customer',
      subtitle: 'Customer number {number}',
      back: 'Back to list',
      loadFailed: 'The customer could not be loaded.',
      detailsFailed: 'Some detail data could not be loaded.',
      tabs: {
        overview: 'Overview',
        contacts: 'Contacts',
        profile: 'Profile',
      },
      card: {
        contact: 'Contact',
        master: 'Master Data',
      },
      fields: {
        country: 'Country',
        phone1: 'Phone 1',
        phone2: 'Phone 2',
        mobile: 'Mobile',
        fax: 'Fax',
        email: 'Email',
        website: 'Website',
        zipCity: 'Zip, City',
      },
      ticket: {
        new: 'New Ticket',
        soonTitle: 'Coming soon',
        soonDetail: 'Creating JIRA tickets will be available in a later version.',
      },
      activity: {
        title: 'Call activity',
        range: 'Last 12 months',
        calls: 'Calls / month',
        duration: 'Duration (min) / month',
        callsTooltip: 'Calls: {count}',
        durationTooltip: 'Duration: {count} min',
      },
      contacts: {
        empty: 'No contacts available.',
        columns: {
          nr: 'No.',
          name: 'Name',
          position: 'Position',
        },
      },
      profile: {
        cardSystem: 'System & ERP',
        cardScope: 'Scope & Licenses',
        cardSupport: 'Support',
        erpSystem: 'ERP system',
        databaseType: 'SQL DB',
        serverOsName: 'Server OS',
        databaseVersion: 'SQL DB version',
        oemVersion: 'OEM version',
        installedVersionLive: 'Inst. version LIVE',
        remoteMaintenance: 'Remote access',
        lastUpdateLive: 'Last update LIVE',
        employeeCount: 'Employees',
        industry: 'Industry',
        workplaces: 'Workplaces',
        systems: 'Systems',
        s4pSolutions: 'S4P solutions',
        maintenanceContract: 'Maintenance contract',
        maintenanceActive: 'Active',
        maintenanceInactive: 'Inactive',
        licenses: 'Licenses',
        salesPerson: 'S4P sales rep',
        projectManager: 'S4P project lead',
        projectTeam: 'S4P project team',
        installationHint: 'Installation note',
      },
    },
  },
  whitelist: {
    title: 'Whitelist',
    placeholder: 'Dummy whitelist management view.',
  },
  callFilter: {
    whitelistTitle: 'New whitelist entry',
    blacklistTitle: 'New blacklist entry',
    editWhitelistTitle: 'Edit whitelist entry',
    editBlacklistTitle: 'Edit blacklist entry',
    explainWhitelist: 'Calls from this target are always put through.',
    explainBlacklist: 'Calls from this target are automatically blocked.',
    target: 'Target',
    targetCustomer: 'Customer',
    targetNumber: 'Number',
    customer: 'Customer',
    customerEmpty: 'No customers found.',
    customerLocked: 'The customer of an existing entry cannot be changed.',
    noCustomer: 'No customer selected',
    columns: {
      number: 'Customer no.',
      name: 'Name',
    },
    number: 'Phone number',
    numberPlaceholder: 'e.g. +49 89 416 140 60',
    numberHint: 'Full number with country code. Without a prefix a German number is assumed.',
    numberLocked: 'The number of an existing entry cannot be changed.',
    noNumber: 'No number',
    preview: 'Preview',
    details: 'Details',
    description: 'Description',
    descriptionPlaceholder: 'e.g. reason for the entry…',
    optional: '(optional)',
    validity: 'Validity',
    ruleActive: 'Rule active',
    expiration: 'Expiration date',
    expirationHint: 'The entry expires at the end of the selected day.',
    active: 'Active',
    cancel: 'Cancel',
    save: 'Save',
    numberRequired: 'Please enter a phone number.',
    customerRequired: 'Please select a customer.',
    expirationRequired: 'Please enter an expiration date.',
    whitelistCreated: 'Whitelist entry created',
    blacklistCreated: 'Blacklist entry created',
    whitelistUpdated: 'Whitelist entry updated',
    blacklistUpdated: 'Blacklist entry updated',
    saveFailed: 'Saving failed. Please try again.',
    list: {
      title: 'Call filter',
      counts: '{whitelist} whitelist · {blacklist} blacklist',
      newEntry: 'New entry',
      newWhitelist: 'Whitelist entry',
      newBlacklist: 'Blacklist entry',
      searchPlaceholder: 'Number or description…',
      kindLabel: 'Filter by list',
      kind: {
        all: 'All',
        whitelist: 'Whitelist',
        blacklist: 'Blacklist',
      },
      statusLabel: 'Status',
      statusAll: 'All statuses',
      results: '{count} entries',
      empty: 'No filter entries yet.',
      loadFailed: 'The entries could not be loaded. Please try again.',
      columns: {
        kind: 'Type',
        number: 'Number',
        description: 'Description',
        status: 'Status',
        expiration: 'Expires',
        created: 'Valid since',
        actions: 'Actions',
      },
      status: {
        active: 'Active',
        inactive: 'Inactive',
        expired: 'Expired',
      },
      edit: 'Edit',
      delete: 'Delete',
      deleteHeader: 'Delete entry?',
      deleteMessage: 'Remove {number} from the list? This cannot be undone.',
      deleteConfirm: 'Delete',
      deleteCancel: 'Cancel',
      deleted: 'Entry deleted',
      deleteFailed: 'The entry could not be deleted. Please try again.',
    },
  },
};

export default en;
